import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    CircularProgress,
    Alert,
    AlertTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Grid,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    LinearProgress,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    TreeView,
    TreeItem,
    Collapse,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import BackupIcon from '@mui/icons-material/Backup';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import StorageIcon from '@mui/icons-material/Storage';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import CloudIcon from '@mui/icons-material/Cloud';
import UsbIcon from '@mui/icons-material/Usb';
import SecurityIcon from '@mui/icons-material/Security';
import CodeIcon from '@mui/icons-material/Code';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import { API_BASE } from '../../apiBase';

// Helper function to build tree structure from flat file list
const buildFileTree = (files) => {
    const tree = {};
    
    files.forEach(file => {
        const parts = file.relative_path.split('/');
        let current = tree;
        
        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            if (i === parts.length - 1) {
                // It's a file
                if (!current._files) current._files = [];
                current._files.push(file);
            } else {
                // It's a directory
                if (!current[part]) {
                    current[part] = {};
                }
                current = current[part];
            }
        }
    });
    
    return tree;
};

// Recursive component to render file tree
const FileTreeNode = ({ nodeKey, node, level = 0 }) => {
    const [expanded, setExpanded] = React.useState(level < 2); // Expand first 2 levels by default
    
    // Check if this node has subdirectories
    const subdirs = Object.entries(node).filter(([k, v]) => k !== '_files' && typeof v === 'object');
    const hasSubdirs = subdirs.length > 0;
    const hasFiles = node._files && node._files.length > 0;
    
    if (!hasSubdirs && !hasFiles) {
        return null;
    }
    
    return (
        <Box key={nodeKey}>
            {nodeKey && (hasSubdirs || hasFiles) && (
                <Box
                    sx={{
                        pl: level * 2,
                        py: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        cursor: hasSubdirs || hasFiles ? 'pointer' : 'default',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        }
                    }}
                    onClick={() => setExpanded(!expanded)}
                >
                    {(hasSubdirs || hasFiles) && (
                        <ChevronRightIcon
                            sx={{
                                mr: 0.5,
                                transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s',
                                width: 20,
                                height: 20,
                            }}
                        />
                    )}
                    {expanded ? (
                        <FolderOpenIcon sx={{ mr: 1, width: 20, height: 20, color: '#ff9800' }} />
                    ) : (
                        <FolderIcon sx={{ mr: 1, width: 20, height: 20, color: '#ff9800' }} />
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {nodeKey}
                    </Typography>
                </Box>
            )}
            
            {expanded && (
                <Box>
                    {/* Render files in this directory */}
                    {node._files && node._files.map(file => (
                        <Box
                            key={file.path}
                            sx={{
                                pl: (level + 1) * 2,
                                py: 0.3,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                <InsertDriveFileIcon sx={{ mr: 1, width: 18, height: 18, color: '#1976d2' }} />
                                <Typography
                                    variant="body2"
                                    sx={{ fontFamily: 'monospace', fontSize: '0.85rem', flex: 1 }}
                                >
                                    {file.name}
                                </Typography>
                            </Box>
                            <Typography
                                variant="caption"
                                sx={{
                                    ml: 1,
                                    color: 'text.secondary',
                                    whiteSpace: 'nowrap',
                                    fontFamily: 'monospace',
                                }}
                            >
                                {file.size_bytes ? `${(file.size_bytes / 1024 / 1024).toFixed(2)} MB` : '0 MB'}
                            </Typography>
                        </Box>
                    ))}
                    
                    {/* Render subdirectories */}
                    {subdirs.map(([key, value]) => (
                        <FileTreeNode key={key} nodeKey={key} node={value} level={level + 1} />
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default function BackupTab({ headers }) {
    const [status, setStatus] = useState(null);
    const [files, setFiles] = useState([]);
    const [policy, setPolicy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [backupInProgress, setBackupInProgress] = useState(false);
    const [verifyInProgress, setVerifyInProgress] = useState(false);
    const [backupResult, setBackupResult] = useState(null);
    const [verifyResult, setVerifyResult] = useState(null);
    const [outputDialog, setOutputDialog] = useState({ open: false, title: '', content: '' });

    useEffect(() => {
        loadBackupStatus();
        loadBackupFiles();
        loadRetentionPolicy();
    }, []);

    const loadBackupStatus = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await fetch(`${API_BASE}/admin/backup/status`, { headers });

            if (!res.ok) {
                throw new Error(`Failed to load backup status: ${res.status}`);
            }

            const data = await res.json();
            setStatus(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadBackupFiles = async () => {
        try {
            const res = await fetch(`${API_BASE}/admin/backup/files`, { headers });
            if (!res.ok) {
                throw new Error(`Failed to load backup files: ${res.status}`);
            }
            const data = await res.json();
            setFiles(data.files || []);
        } catch (err) {
            console.error('Error loading backup files:', err);
            setFiles([]);
        }
    };

    const loadRetentionPolicy = async () => {
        try {
            const res = await fetch(`${API_BASE}/admin/backup/retention-policy`, { headers });
            if (res.ok) {
                const data = await res.json();
                setPolicy(data);
            }
        } catch (err) {
            console.error('Error loading retention policy:', err);
        }
    };

    const triggerBackup = async (type = 'single') => {
        try {
            setBackupInProgress(true);
            setBackupResult(null);

            const res = await fetch(`${API_BASE}/admin/backup/trigger`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ type })
            });

            const data = await res.json();
            setBackupResult(data);

            if (data.success) {
                // Reload status after successful backup
                setTimeout(() => loadBackupStatus(), 2000);
            }

            // Show output dialog
            setOutputDialog({
                open: true,
                title: data.success ? 'Backup Completed' : 'Backup Failed',
                content: data.output || data.message
            });
        } catch (err) {
            setBackupResult({ success: false, message: err.message });
        } finally {
            setBackupInProgress(false);
        }
    };

    const triggerVerify = async () => {
        try {
            setVerifyInProgress(true);
            setVerifyResult(null);

            const res = await fetch(`${API_BASE}/admin/backup/verify`, {
                method: 'POST',
                headers
            });

            const data = await res.json();
            setVerifyResult(data);

            // Show output dialog
            setOutputDialog({
                open: true,
                title: data.success ? 'Verification Passed' : 'Verification Issues Found',
                content: data.output || data.message
            });

            // Reload status after verification
            if (data.success) {
                setTimeout(() => loadBackupStatus(), 1000);
            }
        } catch (err) {
            setVerifyResult({ success: false, message: err.message });
        } finally {
            setVerifyInProgress(false);
        }
    };

    const formatDate = (isoString) => {
        if (!isoString) return 'N/A';
        const date = new Date(isoString);
        return date.toLocaleString();
    };

    const formatSize = (mb) => {
        if (mb < 1024) return `${mb} MB`;
        return `${(mb / 1024).toFixed(2)} GB`;
    };

    const getHealthColor = (health) => {
        switch (health) {
            case 'healthy': return 'success';
            case 'warning': return 'warning';
            case 'error': return 'error';
            default: return 'default';
        }
    };

    const getHealthIcon = (health) => {
        switch (health) {
            case 'healthy': return <CheckCircleIcon />;
            case 'warning': return <WarningIcon />;
            case 'error': return <ErrorIcon />;
            default: return <InfoIcon />;
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={2}>
                <Alert severity="error">
                    <AlertTitle>Error Loading Backup Status</AlertTitle>
                    {error}
                </Alert>
                <Button startIcon={<RefreshIcon />} onClick={loadBackupStatus} sx={{ mt: 2 }}>
                    Retry
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            {/* Header with Actions */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5">Backup & Recovery</Typography>
                <Box>
                    <Button
                        startIcon={<RefreshIcon />}
                        onClick={loadBackupStatus}
                        disabled={loading}
                        sx={{ mr: 1 }}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<VerifiedUserIcon />}
                        onClick={triggerVerify}
                        disabled={verifyInProgress || !status?.summary?.total_backups || status?.management_mode === 'status-only'}
                        sx={{ mr: 1 }}
                    >
                        {verifyInProgress ? 'Verifying...' : 'Verify Backup'}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<BackupIcon />}
                        onClick={() => triggerBackup('multi-generation')}
                        disabled={backupInProgress || status?.management_mode === 'status-only'}
                    >
                        {backupInProgress ? 'Creating Backup...' : 'Create Backup'}
                    </Button>
                </Box>
            </Box>

            {status?.management_mode === 'status-only' && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    <AlertTitle>Automated on the production host</AlertTitle>
                    Backups run daily at 02:00 UTC (04:00 Zurich during summer time) and verification runs at 02:30 UTC (04:30 Zurich).
                    This dashboard is read-only; use the host maintenance procedure for manual operations.
                </Alert>
            )}

            {/* ISDS Compliance Notice */}
            <Alert severity="info" sx={{ mb: 3 }}>
                <AlertTitle>ISDS A2-4.3.7 & A2-4.3.8 Compliance</AlertTitle>
                Multi-generation backups (daily/weekly/monthly) with offline WORM storage and ransomware protection.
                This interface manages Tier 1 (standard backups). For Tier 2 (offline) and Tier 3 (cloud WORM), use the scripts below.
            </Alert>

            {/* Backup Result Messages */}
            {backupResult && (
                <Alert
                    severity={backupResult.success ? 'success' : 'error'}
                    sx={{ mb: 2 }}
                    onClose={() => setBackupResult(null)}
                >
                    {backupResult.message}
                </Alert>
            )}

            {verifyResult && (
                <Alert
                    severity={verifyResult.success ? 'success' : 'warning'}
                    sx={{ mb: 2 }}
                    onClose={() => setVerifyResult(null)}
                >
                    {verifyResult.message}
                    {verifyResult.metrics && (
                        <Box mt={1}>
                            <Typography variant="body2">
                                Errors: {verifyResult.metrics.errors} | Warnings: {verifyResult.metrics.warnings}
                            </Typography>
                        </Box>
                    )}
                </Alert>
            )}

            {/* Status Overview Cards */}
            <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={1}>
                                <StorageIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">Total Backups</Typography>
                            </Box>
                            <Typography variant="h4">{status?.summary?.total_backups || 0}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Daily: {status?.summary?.daily_count || 0} |
                                Weekly: {status?.summary?.weekly_count || 0} |
                                Monthly: {status?.summary?.monthly_count || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={1}>
                                {getHealthIcon(status?.summary?.backup_health)}
                                <Typography variant="h6" ml={1}>Health Status</Typography>
                            </Box>
                            <Chip
                                label={status?.summary?.backup_health || 'unknown'}
                                color={getHealthColor(status?.summary?.backup_health)}
                                sx={{ textTransform: 'capitalize' }}
                            />
                            <Typography variant="body2" color="text.secondary" mt={1}>
                                Latest: {status?.summary?.latest_backup_age_hours ?
                                    `${status.summary.latest_backup_age_hours.toFixed(1)}h ago` : 'N/A'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" mb={1}>Disk Usage</Typography>
                            <Typography variant="h4">
                                {status?.disk_usage?.total_gb ?
                                    `${status.disk_usage.total_gb.toFixed(2)} GB` : 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Free: {status?.disk_usage?.free_gb?.toFixed(1) || 'N/A'} GB
                                ({status?.disk_usage?.free_percent || 'N/A'}%)
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" mb={1}>Last Verification</Typography>
                            <Chip
                                label={status?.latest_verification?.status || 'Not Run'}
                                color={status?.latest_verification?.status === 'PASS' ? 'success' : 'default'}
                                size="small"
                            />
                            <Typography variant="body2" color="text.secondary" mt={1}>
                                {status?.latest_verification?.date || 'Never verified'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Accordion defaultExpanded sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <StorageIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Backup Files on VM</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {files.length === 0 ? (
                        <Alert severity="info">No backup files found in the local backup directory.</Alert>
                    ) : (
                        <Paper
                            sx={{
                                p: 2,
                                backgroundColor: '#fafafa',
                                border: '1px solid #e0e0e0',
                                fontFamily: 'monospace',
                            }}
                        >
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {files.length} files found
                                </Typography>
                            </Box>
                            <FileTreeNode nodeKey="" node={buildFileTree(files)} level={0} />
                        </Paper>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* Retention Policy */}
            {policy && (
                <Accordion defaultExpanded sx={{ mb: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">Backup Retention Policy</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            {Object.entries(policy.generations).map(([gen, info]) => (
                                <Grid item xs={12} md={4} key={gen}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="subtitle1" fontWeight="bold" textTransform="capitalize">
                                            {gen}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Frequency: {info.frequency}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Retention: {info.description}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                        <Box mt={2}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>RPO:</strong> {policy.rpo_hours} hours |
                                <strong> RTO:</strong> {policy.rto_minutes} minutes
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mt={1}>
                                <strong>Automated:</strong> {policy.automated ? 'Yes' : 'No - Setup required'} |
                                {!policy.automated && (
                                    <span> {policy.automation_instructions}</span>
                                )}
                            </Typography>
                        </Box>
                    </AccordionDetails>
                </Accordion>
            )}

            {/* Backup Generations */}
            {['daily', 'weekly', 'monthly'].map((generation) => {
                const backups = status?.generations?.[generation]?.backups || [];
                if (backups.length === 0) return null;

                return (
                    <Accordion key={generation} sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h6" textTransform="capitalize">
                                {generation} Backups ({backups.length})
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Backup Name</TableCell>
                                            <TableCell>Created</TableCell>
                                            <TableCell>Age</TableCell>
                                            <TableCell>Size</TableCell>
                                            <TableCell>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {backups.map((backup) => (
                                            <TableRow key={backup.name}>
                                                <TableCell>
                                                    <Typography variant="body2" fontFamily="monospace">
                                                        {backup.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{formatDate(backup.created)}</TableCell>
                                                <TableCell>{backup.age_hours.toFixed(1)}h</TableCell>
                                                <TableCell>{formatSize(backup.size_mb)}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={backup.complete ? 'Complete' : 'Incomplete'}
                                                        color={backup.complete ? 'success' : 'warning'}
                                                        icon={backup.complete ? <CheckCircleIcon /> : <WarningIcon />}
                                                    />
                                                    {!backup.files?.database && (
                                                        <Chip size="small" label="No DB" color="error" sx={{ ml: 0.5 }} />
                                                    )}
                                                    {!backup.files?.uploads && (
                                                        <Chip size="small" label="No Files" color="error" sx={{ ml: 0.5 }} />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>
                );
            })}

            {/* Warning if no backups */}
            {status?.summary?.total_backups === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    <AlertTitle>No Backups Found</AlertTitle>
                    {status?.management_mode === 'status-only'
                        ? 'No host backups are currently visible to the backend container.'
                        : 'No backups have been created yet. Click "Create Backup" to create your first backup.'}
                </Alert>
            )}

            {/* Warning if backup is old */}
            {status?.summary?.latest_backup_age_hours > 48 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    <AlertTitle>Backup Out of Date</AlertTitle>
                    The latest backup is {status.summary.latest_backup_age_hours.toFixed(1)} hours old.
                    Consider setting up automated backups or creating a new backup.
                </Alert>
            )}

            {/* Docker Volumes Info */}
            {status?.docker_volumes && status.docker_volumes.length > 0 && (
                <Paper sx={{ p: 2, mt: 2 }}>
                    <Typography variant="h6" mb={1}>Docker Volumes</Typography>
                    <Box>
                        {status.docker_volumes.map((vol) => (
                            <Chip key={vol} label={vol} size="small" sx={{ mr: 1, mb: 1 }} />
                        ))}
                    </Box>
                </Paper>
            )}

            {/* ISDS A2-4.3.8: Offline and WORM Backup Information */}
            <Paper sx={{ p: 3, mt: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                    <SecurityIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                    <Typography variant="h6">ISDS A2-4.3.8: Ransomware-Protected Backups</Typography>
                </Box>

                <Alert severity="warning" sx={{ mb: 3 }}>
                    <AlertTitle>Business-Critical Data Protection</AlertTitle>
                    In addition to regular backups above, ISDS A2-4.3.8 requires offline storage with WORM
                    (Write Once Read Many) or physical decoupling to protect against ransomware attacks.
                </Alert>

                <Grid container spacing={3}>
                    {/* Tier 2: Offline Backup */}
                    <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <UsbIcon color="secondary" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Tier 2: Offline Backup</Typography>
                                </Box>

                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Create immutable backups on external media with physical decoupling from network.
                                </Typography>

                                <Divider sx={{ my: 2 }} />

                                <Typography variant="subtitle2" gutterBottom>Features:</Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary="External USB/NAS storage"
                                            secondary="Physically disconnect after backup"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary="Filesystem immutability (chattr +i)"
                                            secondary="Cannot be modified or deleted"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary="SHA256 integrity checksums"
                                            secondary="Detects tampering"
                                        />
                                    </ListItem>
                                </List>

                                <Divider sx={{ my: 2 }} />

                                <Typography variant="subtitle2" gutterBottom>
                                    <CodeIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                    Usage:
                                </Typography>
                                <Paper sx={{ p: 1.5, bgcolor: '#f5f5f5', mt: 1 }}>
                                    <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                                        # Connect external drive (e.g., /mnt/backup or E:\)<br/>
                                        ./scripts/backup-offline.sh /mnt/external-backup<br/>
                                        <br/>
                                        # After completion, disconnect media<br/>
                                        # Store in fireproof safe
                                    </Typography>
                                </Paper>

                                <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                                    <strong>Recommended:</strong> Weekly on Sundays (automated via cron/Task Scheduler)
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Tier 3: Cloud WORM */}
                    <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <CloudIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Tier 3: Cloud WORM</Typography>
                                </Box>

                                <Typography variant="body2" color="text.secondary" paragraph>
                                    True WORM compliance with cloud object lock (AWS S3, Azure, Backblaze B2).
                                </Typography>

                                <Divider sx={{ my: 2 }} />

                                <Typography variant="subtitle2" gutterBottom>Features:</Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary="True WORM technology"
                                            secondary="Regulatory compliant (SEC, FINRA)"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary="Retention enforcement"
                                            secondary="Cannot delete until expiration"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary="Off-site protection"
                                            secondary="Geographically distributed"
                                        />
                                    </ListItem>
                                </List>

                                <Divider sx={{ my: 2 }} />

                                <Typography variant="subtitle2" gutterBottom>
                                    <CodeIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                    Usage:
                                </Typography>
                                <Paper sx={{ p: 1.5, bgcolor: '#f5f5f5', mt: 1 }}>
                                    <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                                        # AWS S3 with Object Lock<br/>
                                        ./scripts/backup-cloud-worm.sh aws \<br/>
                                        &nbsp;&nbsp;s3://bucket/floco<br/>
                                        <br/>
                                        # Azure Blob Immutable Storage<br/>
                                        ./scripts/backup-cloud-worm.sh azure \<br/>
                                        &nbsp;&nbsp;floco-backups<br/>
                                        <br/>
                                        # Backblaze B2 (cost-effective)<br/>
                                        ./scripts/backup-cloud-worm.sh b2 \<br/>
                                        &nbsp;&nbsp;floco-backups
                                    </Typography>
                                </Paper>

                                <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                                    <strong>Recommended:</strong> Monthly on 1st (90-day retention)
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Verification */}
                    <Grid item xs={12}>
                        <Card variant="outlined" sx={{ bgcolor: '#f9f9f9' }}>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <VerifiedUserIcon color="success" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Backup Verification</Typography>
                                </Box>

                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Verify integrity and immutability of offline and cloud WORM backups.
                                </Typography>

                                <Paper sx={{ p: 1.5, bgcolor: 'white', mt: 2 }}>
                                    <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                                        # Verify offline backup<br/>
                                        ./scripts/verify-worm-backup.sh \<br/>
                                        &nbsp;&nbsp;/mnt/external/floco_offline_backups/latest<br/>
                                        <br/>
                                        # Verify cloud WORM backup<br/>
                                        ./scripts/verify-worm-backup.sh \<br/>
                                        &nbsp;&nbsp;s3://bucket/floco/floco_worm_20260718_040000<br/>
                                        <br/>
                                        # Check immutability status (Linux)<br/>
                                        lsattr /mnt/external/floco_offline_backups/latest/*
                                    </Typography>
                                </Paper>

                                <Alert severity="info" sx={{ mt: 2 }}>
                                    <Typography variant="body2">
                                        <strong>Testing Schedule:</strong> Weekly verification + Monthly restore test (see ISDS/A2-4.3.8_Business_Critical_Backup.md)
                                    </Typography>
                                </Alert>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Documentation */}
                    <Grid item xs={12}>
                        <Alert severity="success">
                            <AlertTitle>Complete Documentation</AlertTitle>
                            Full implementation details, operational procedures, and compliance checklists available in:
                            <Typography variant="body2" fontFamily="monospace" sx={{ mt: 1 }}>
                                ISDS/A2-4.3.8_Business_Critical_Backup.md
                            </Typography>
                        </Alert>
                    </Grid>
                </Grid>
            </Paper>

            {/* Output Dialog */}
            <Dialog
                open={outputDialog.open}
                onClose={() => setOutputDialog({ ...outputDialog, open: false })}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>{outputDialog.title}</DialogTitle>
                <DialogContent>
                    <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                        <Typography
                            component="pre"
                            sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                maxHeight: '400px',
                                overflow: 'auto'
                            }}
                        >
                            {outputDialog.content}
                        </Typography>
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOutputDialog({ ...outputDialog, open: false })}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
